import { defineStore } from 'pinia';
import { z } from 'zod';
import { computed, ref } from 'vue';
import { cloneDeep } from 'lodash-es';
import { StoreNames } from '../../../shared/store-names';
import { useAccountStore } from '../../account';
import {
  selectFilterValidationSchema,
  multiSelectFilterValidationSchema,
  rangeFilterValidationSchema,
} from '../../filters';
import { useDataTableSettings } from '../../../composables';
import { useUiStatesApi, useUiStateAutoPersist } from '../../ui-states';
import { ProductLocationsColumns as Columns } from '../shared/product-locations-columns';
import type { ProductLocationsFiltersState } from '../interfaces';

const UI_STATES_KEY = StoreNames.InventoryProductLocationsPage;

export const useInventoryProductLocationsPageStore = defineStore(StoreNames.InventoryProductLocationsPage, () => {
  const defaultAppliedFilters: ProductLocationsFiltersState = {
    location_id: ['in', []],
    product_id: ['in', []],
    is_store_transfer_enabled: ['eq', null],
    product_assortment_group_name: ['in', []],
    product_season_names: ['in', []],
    product_brand_names: ['in', []],
    product_category_names: ['in', []],
    product_department_names: ['in', []],
    product_price: ['in_range', [null, null]],
    product_global_sale_rate: ['in_range', [null, null]],
  };

  const appliedFilters = ref<ProductLocationsFiltersState>(cloneDeep(defaultAppliedFilters));

  const filtersValidationSchema = z.object({
    location_id: multiSelectFilterValidationSchema,
    product_id: multiSelectFilterValidationSchema,
    is_store_transfer_enabled: selectFilterValidationSchema,
    product_assortment_group_name: multiSelectFilterValidationSchema,
    product_season_names: multiSelectFilterValidationSchema,
    product_brand_names: multiSelectFilterValidationSchema,
    product_category_names: multiSelectFilterValidationSchema,
    product_department_names: multiSelectFilterValidationSchema,
    product_price: rangeFilterValidationSchema,
    product_global_sale_rate: rangeFilterValidationSchema,
  });

  const quickFilters = ref<string[]>([]);

  const {
    columnsVisibility,
    columnsWidth,
    columnsOrder,
    visibleColumns,
    resetColumnsSettings,
    applyPersistedState,
    columnsSettingsTouched,
    pinnedColumns,
    sorting,
    resetSorting,
    handleColumnResize,
    userColumnsWidth,
  } = useDataTableSettings({
    defaultColumnsVisibility: {
      [Columns.ProductName]: true,
      [Columns.ProductExternalId]: false,
      [Columns.LocationName]: true,
      [Columns.LocationExternalId]: false,
      [Columns.StoreTransferEnabled]: true,
      [Columns.Price]: false,
      [Columns.SaleRate]: false,
      [Columns.ProductGlobalSaleRate]: false,
      [Columns.Brands]: false,
      [Columns.Categories]: false,
      [Columns.Seasons]: false,
      [Columns.DepartmentName]: false,
      [Columns.AG]: false,
    },
    defaultColumnsWidth: {
      [Columns.ProductName]: 256,
      [Columns.ProductExternalId]: 256,
      [Columns.LocationName]: 256,
      [Columns.LocationExternalId]: 256,
      [Columns.StoreTransferEnabled]: 256,
      [Columns.Price]: 168,
      [Columns.SaleRate]: 168,
      [Columns.ProductGlobalSaleRate]: 168,
      [Columns.Brands]: 168,
      [Columns.Categories]: 168,
      [Columns.Seasons]: 168,
      [Columns.DepartmentName]: 168,
      [Columns.AG]: 168,
    },
    defaultColumnsOrder: [
      Columns.ProductName,
      Columns.ProductExternalId,
      Columns.LocationName,
      Columns.LocationExternalId,
      Columns.StoreTransferEnabled,
      Columns.Price,
      Columns.SaleRate,
      Columns.ProductGlobalSaleRate,
      Columns.Brands,
      Columns.Categories,
      Columns.Seasons,
      Columns.DepartmentName,
      Columns.AG,
    ],
    defaultPinnedColumns: [],
  });

  function reset() {
    resetColumnsSettings();
    resetSorting();
    appliedFilters.value = cloneDeep(defaultAppliedFilters);
  }

  const api = useUiStatesApi();
  const accountStore = useAccountStore();

  const fetching = ref(false);
  const fetched = ref(false);

  let fetchingPromise: Promise<void> | null = null;

  async function fetch(force = false) {
    if (fetched.value && !force) {
      return fetchingPromise ?? Promise.resolve();
    }

    if (!fetchingPromise) {
      fetchingPromise = (async () => {
        fetching.value = true;

        const [persistedState] = await Promise.all([
          api.getUiStates({ key: UI_STATES_KEY }).then(({ data }) => data.data[0]),
          accountStore.fetch(),
        ]);

        reset();

        if (persistedState) {
          applyPersistedState(persistedState.value);

          if (persistedState.value.appliedFilters) {
            try {
              appliedFilters.value = filtersValidationSchema.parse(persistedState.value.appliedFilters);
            } catch (error) {
              // do nothing
            }
          }

          if (persistedState.value.quickFilters) {
            quickFilters.value = persistedState.value.quickFilters;
          }
        }

        fetching.value = false;
        fetched.value = true;
        fetchingPromise = null;
      })();
    }

    return fetchingPromise;
  }

  const persistingValue = computed(() => ({
    columnsVisibility: { ...columnsVisibility.value },
    columnsWidth: { ...userColumnsWidth.value },
    columnsOrder: [...columnsOrder.value],
    pinnedColumns: [...pinnedColumns.value],
    sorting: { ...sorting.value },
    appliedFilters: { ...appliedFilters.value },
    quickFilters: [...quickFilters.value],
  }));

  useUiStateAutoPersist({
    enabled: fetched,
    value: persistingValue,
    key: UI_STATES_KEY,
  });

  return {
    fetching,
    fetched,
    fetch,
    reset,
    resetColumnsSettings,
    columnsVisibility,
    columnsWidth,
    visibleColumns,
    columnsOrder,
    pinnedColumns,
    sorting,
    appliedFilters,
    columnsSettingsTouched,
    handleColumnResize,
    resetSorting,
    quickFilters,
  };
});
